import { useMemo, useState, type SubmitEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { formatDistanceToNow } from 'date-fns';
import { AlertCircle, Check, ChevronDown, EyeOff, MoreHorizontal, Plus } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import {
  type CreateIncidentRequest,
  type Incident,
  type IncidentSeverity,
  type IncidentStatus,
  type ListIncidentsFilters,
  useAcknowledgeIncident,
  useArchiveIncident,
  useCreateIncident,
  useIncidentsPage,
  useResolveIncident,
} from '@/hooks/useIncidents';
import { useDivisions } from '@/hooks/useDivisions';
import { useRegions } from '@/hooks/useRegions';
import { PageContainer } from '@/components/ui/page-container';
import { PageHeader } from '@/components/ui/page-header';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { EmptyState } from '@/components/ui/empty-state';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ResponsiveTable, type ResponsiveTableColumn } from '@/components/ui/responsive-table';
import { AppSheet } from '@/components/ui/app-sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { SeverityBadge } from '@/components/SeverityBadge.tsx';
import { QuietBadge } from '@/components/incidents/QuietBadge.tsx';
import {
  ALL_SEVERITIES,
  DEFAULT_SEVERITIES,
  SEVERITY_LABELS,
  severitySummary,
} from './incidentSeverity';

/**
 * Incidents — primary triage view. Spec §4 MF (oncall-critical).
 *
 * Filtering layered in two passes:
 *   1. Server-side via `filters` (status / severity / source / regionId
 *      / includeArchived). Hits the /api/incidents endpoint directly.
 *   2. Client-side toggle for the visibility cut spec §10.3 calls out
 *      as load-bearing:
 *        • Unresolved-only (default ON) — hide `status=resolved` rows
 *          unless the user picks "resolved" from the Status filter or
 *          flips the toggle off.
 *
 * Severity is a *set*, not one value: a checkbox per severity, defaulting
 * to everything except `info`. That default is the old "Show notifications"
 * toggle, which used to be a second control fighting the severity dropdown
 * — "All severities" left `severity` unset, which did not override the
 * toggle, so info rows stayed hidden with nothing on screen saying so. One
 * control cannot contradict itself, and the trigger names the live set
 * ("All except Info") rather than claiming "All". Per-severity counts come
 * from the server computed *without* the severity filter, so an unticked
 * box still shows what it is holding back.
 *
 * Row actions per status (preserved from Bulma):
 *   created      → [Acknowledge] [Resolve]
 *   acknowledged → [Resolve]
 *   resolved     → [Archive] (when not yet archived)
 *
 * Create flow ships as an AppSheet (Sheet at mobile, Dialog at md+)
 * with a small react-hook-form-less form — all four fields plus the
 * optional Division/Region pickers.
 */

export default function Incidents() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const organizationId = user?.organizationId ?? undefined;

  const [filters, setFilters] = useState<ListIncidentsFilters>({});
  const [severities, setSeverities] = useState<IncidentSeverity[]>(DEFAULT_SEVERITIES);
  const [hideResolved, setHideResolved] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const [formTitle, setFormTitle] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formSeverity, setFormSeverity] = useState<IncidentSeverity>('medium');
  const [formDivisionId, setFormDivisionId] = useState<string>('');
  const [formRegionId, setFormRegionId] = useState<string>('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  const PER_PAGE = 50;
  const [page, setPage] = useState(1);

  // Push the visibility toggles into the server query so pagination is exact
  // (no client-side post-filtering of a page). Explicit status/severity
  // filters take precedence over the toggles.
  const serverFilters = useMemo<ListIncidentsFilters>(
    () => ({
      ...filters,
      excludeResolved: hideResolved && filters.status !== 'resolved' ? true : undefined,
      // All boxes ticked is the same query as no severity filter; send it that
      // way so the server does not build a five-way IN for "everything".
      severities: severities.length === ALL_SEVERITIES.length ? undefined : severities,
      page,
      perPage: PER_PAGE,
    }),
    [filters, hideResolved, severities, page],
  );

  const { data: pageData, isLoading, isError, error, isPlaceholderData } = useIncidentsPage(organizationId, serverFilters);
  const visibleIncidents = pageData?.incidents ?? [];
  const total = pageData?.total ?? 0;
  const severityCounts = pageData?.severityCounts ?? {};
  // What the severity selection is currently withholding. Drives the
  // empty-state escape hatch, so an empty list never leaves the user
  // wondering whether the page is broken or just filtered.
  const hiddenBySeverity = ALL_SEVERITIES.filter((s) => !severities.includes(s)).reduce(
    (n, s) => n + (severityCounts[s] ?? 0),
    0,
  );
  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  // Any change to the filters/toggles resets to the first page.
  const filterSignature = JSON.stringify({ ...filters, hideResolved, severities });
  const [pagedSignature, setPagedSignature] = useState(filterSignature);
  if (filterSignature !== pagedSignature) {
    setPagedSignature(filterSignature);
    setPage(1);
  }

  const { data: divisions } = useDivisions(organizationId);
  const { data: regions } = useRegions(organizationId);

  const createMutation = useCreateIncident();
  const acknowledgeMutation = useAcknowledgeIncident();
  const resolveMutation = useResolveIncident();
  const archiveMutation = useArchiveIncident();


  const handleCreateSubmit = async (e?: SubmitEvent) => {
    e?.preventDefault();
    setSubmitError(null);

    const next: CreateIncidentRequest = {
      title: formTitle,
      description: formDescription || undefined,
      severity: formSeverity,
      divisionId: formDivisionId || undefined,
      regionId: formRegionId || undefined,
    };
    try {
      await createMutation.mutateAsync(next);
      setIsCreateOpen(false);
      setFormTitle('');
      setFormDescription('');
      setFormSeverity('medium');
      setFormDivisionId('');
      setFormRegionId('');
    } catch {
      setSubmitError('Failed to create incident.');
    }
  };

  const columns = useMemo<readonly ResponsiveTableColumn<Incident>[]>(
    () => [
      {
        key: 'title',
        label: 'Title',
        hideMobileLabel: true,
        render: (i) => (
          <div className="min-w-0">
            <div className="flex min-w-0 items-center gap-2">
              <Link
                to={`/incidents/${i.id}`}
                className="font-medium hover:underline truncate min-w-0"
              >
                {i.title}
              </Link>
              {i.quiet ? <QuietBadge /> : null}
            </div>
            <RollupNote incident={i} />
            {i.description ? (
              <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                {i.description}
              </p>
            ) : null}
          </div>
        ),
      },
      {
        key: 'severity',
        label: 'Severity',
        render: (i) => <SeverityBadge severity={i.severity} />,
      },
      {
        key: 'source',
        label: 'Source',
        hideAt: 'mobile',
        render: (i) => (
          <span className="text-xs text-muted-foreground">
            {i.source === 'automated' ? 'Auto' : 'Manual'}
          </span>
        ),
      },
      {
        key: 'status',
        label: 'Status',
        render: (i) => <StatusBadge status={i.status} />,
      },
      {
        key: 'createdAt',
        label: 'Created',
        hideAt: 'mobile',
        render: (i) => (
          <span className="text-xs text-muted-foreground">
            {formatDistanceToNow(new Date(i.createdAt), { addSuffix: true })}
          </span>
        ),
      },
    ],
    [],
  );

  if (isError) {
    return (
      <PageContainer>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Error loading incidents: {error instanceof Error ? error.message : 'Unknown error'}
          </AlertDescription>
        </Alert>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="Incidents"
        subtitle="Acknowledge, resolve, and triage everything that pages oncall"
        primary={
          <Button onClick={() => { setIsCreateOpen(true); }}>
            <Plus className="w-4 h-4" />
            <span className="hidden md:inline">Create incident</span>
            <span className="md:hidden">New</span>
          </Button>
        }
      />

      {/* Filter row */}
      <div className="bg-card border border-border rounded-lg p-4 mb-4">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="inc-status-filter" className="text-xs">Status</Label>
            <Select
              value={filters.status ?? 'all'}
              onValueChange={(v) => {
                setFilters({ ...filters, status: v === 'all' ? undefined : v });
              }}
            >
              <SelectTrigger id="inc-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="created">Created</SelectItem>
                <SelectItem value="acknowledged">Acknowledged</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="inc-severity-filter" className="text-xs">Severity</Label>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  id="inc-severity-filter"
                  variant="outline"
                  className="w-full justify-between font-normal"
                >
                  <span className="truncate">{severitySummary(severities)}</span>
                  <ChevronDown className="size-4 shrink-0 opacity-50" aria-hidden />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                <div className="flex items-center gap-1 px-2 py-1.5">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs"
                    disabled={severities.length === ALL_SEVERITIES.length}
                    onClick={() => { setSeverities([...ALL_SEVERITIES]); }}
                  >
                    All
                  </Button>
                  <span className="text-muted-foreground text-xs">·</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs"
                    disabled={
                      severities.length === DEFAULT_SEVERITIES.length &&
                      DEFAULT_SEVERITIES.every((s) => severities.includes(s))
                    }
                    onClick={() => { setSeverities([...DEFAULT_SEVERITIES]); }}
                  >
                    Reset
                  </Button>
                </div>
                <DropdownMenuSeparator />
                {ALL_SEVERITIES.map((sev) => (
                  <DropdownMenuCheckboxItem
                    key={sev}
                    checked={severities.includes(sev)}
                    // Radix closes the menu on select; keep it open so the
                    // whole set can be built in one visit.
                    onSelect={(e) => { e.preventDefault(); }}
                    // Refuse to empty the set. An empty selection would send no
                    // severity param at all, which the server reads as "no
                    // severity filter" and answers with every incident — a
                    // control labelled "No severities" showing all of them,
                    // which is the same class of lie this filter replaced.
                    disabled={severities.length === 1 && severities[0] === sev}
                    onCheckedChange={(checked) => {
                      setSeverities((prev) =>
                        checked
                          ? ALL_SEVERITIES.filter((s) => s === sev || prev.includes(s))
                          : prev.filter((s) => s !== sev),
                      );
                    }}
                  >
                    <span className="flex w-full items-center justify-between gap-3">
                      <span>{SEVERITY_LABELS[sev]}</span>
                      <span className="text-muted-foreground tabular-nums text-xs">
                        {severityCounts[sev] ?? 0}
                      </span>
                    </span>
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="inc-source-filter" className="text-xs">Source</Label>
            <Select
              value={filters.source ?? 'all'}
              onValueChange={(v) => {
                setFilters({
                  ...filters,
                  source: v === 'all' ? undefined : (v as 'manual' | 'automated'),
                });
              }}
            >
              <SelectTrigger id="inc-source-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All sources</SelectItem>
                <SelectItem value="manual">Manual</SelectItem>
                <SelectItem value="automated">Automated</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="inc-quiet-filter" className="text-xs">Notifications</Label>
            <Select
              value={filters.quiet === undefined ? 'all' : filters.quiet ? 'quiet' : 'notifying'}
              onValueChange={(v) => {
                setFilters({ ...filters, quiet: v === 'all' ? undefined : v === 'quiet' });
              }}
            >
              <SelectTrigger id="inc-quiet-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Quiet and notifying</SelectItem>
                <SelectItem value="notifying">Notifying only</SelectItem>
                <SelectItem value="quiet">Quiet only</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="inc-region-filter" className="text-xs">Region</Label>
            <Select
              value={filters.regionId ?? 'all'}
              onValueChange={(v) => {
                setFilters({ ...filters, regionId: v === 'all' ? undefined : v });
              }}
            >
              <SelectTrigger id="inc-region-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All regions</SelectItem>
                {regions?.map((r) => (
                  <SelectItem key={r.id} value={r.id}>
                    {r.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Visibility toggles row */}
        <div className="flex flex-wrap items-center gap-4 mt-4 pt-3 border-t border-border-subtle">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox
              id="hide-resolved"
              checked={hideResolved}
              onCheckedChange={(v) => { setHideResolved(v === true); }}
            />
            <span>Unresolved only</span>
          </label>


          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox
              id="include-rolled-up"
              checked={filters.includeRolledUp ?? false}
              onCheckedChange={(v) => {
                setFilters({ ...filters, includeRolledUp: v === true });
              }}
            />
            <span>Show rolled-up</span>
          </label>

          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox
              id="include-archived"
              checked={filters.includeArchived ?? false}
              onCheckedChange={(v) => {
                setFilters({ ...filters, includeArchived: v === true });
              }}
            />
            <span>Show archived</span>
          </label>
        </div>
      </div>

      <ResponsiveTable<Incident>
        columns={columns}
        rows={visibleIncidents}
        rowKey={(r) => r.id}
        isLoading={isLoading}
        onRowClick={(r) => { void navigate(`/incidents/${r.id}`); }}
        rowActions={(incident) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Actions for ${incident.title}`}
                onClick={(e) => { e.stopPropagation(); }}
              >
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              onClick={(e) => { e.stopPropagation(); }}
            >
              {incident.status === 'created' ? (
                <DropdownMenuItem
                  onClick={() => { acknowledgeMutation.mutate(incident.id); }}
                  disabled={acknowledgeMutation.isPending}
                  className="gap-2"
                >
                  <Check className="w-4 h-4" />
                  Acknowledge
                </DropdownMenuItem>
              ) : null}
              {incident.status === 'created' || incident.status === 'acknowledged' ? (
                <DropdownMenuItem
                  onClick={() => { resolveMutation.mutate(incident.id); }}
                  disabled={resolveMutation.isPending}
                  className="gap-2"
                >
                  <Check className="w-4 h-4" />
                  Resolve
                </DropdownMenuItem>
              ) : null}
              {incident.status === 'resolved' && !incident.archivedAt ? (
                <DropdownMenuItem
                  onClick={() => { archiveMutation.mutate(incident.id); }}
                  disabled={archiveMutation.isPending}
                  className="gap-2"
                >
                  <EyeOff className="w-4 h-4" />
                  Archive
                </DropdownMenuItem>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        empty={
          <EmptyState
            variant="no-data"
            title="No incidents to show"
            description={
              <span>
                {hideResolved ? (
                  <button
                    type="button"
                    className="text-primary hover:underline"
                    onClick={() => { setHideResolved(false); }}
                  >
                    Show resolved
                  </button>
                ) : null}
                {hiddenBySeverity > 0 ? (
                  <>
                    {hideResolved ? ' · ' : ''}
                    <button
                      type="button"
                      className="text-primary hover:underline"
                      onClick={() => { setSeverities([...ALL_SEVERITIES]); }}
                    >
                      Show all {hiddenBySeverity} hidden by severity
                    </button>
                  </>
                ) : null}
              </span>
            }
          />
        }
      />

      {total > PER_PAGE ? (
        <div className="flex items-center justify-between gap-3 mt-4 text-sm">
          <span className="text-muted-foreground">
            {(page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, total)} of {total}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1 || isPlaceholderData}
              onClick={() => { setPage((p) => Math.max(1, p - 1)); }}
            >
              Previous
            </Button>
            <span className="text-muted-foreground">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages || isPlaceholderData}
              onClick={() => { setPage((p) => Math.min(totalPages, p + 1)); }}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}

      {/* Create incident — AppSheet (Sheet on mobile, Dialog at md+) */}
      <AppSheet
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        title="Create incident"
        description="Capture a manual incident — auto-incidents come from alert rules."
      >
        <AppSheet.Body>
          <form
            id="create-incident-form"
            onSubmit={(e) => { void handleCreateSubmit(e); }}
            className="space-y-4"
          >
            {submitError ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{submitError}</AlertDescription>
              </Alert>
            ) : null}

            <div className="space-y-1.5">
              <Label htmlFor="ci-title">
                Title <span className="text-destructive">*</span>
              </Label>
              <Input
                id="ci-title"
                value={formTitle}
                onChange={(e) => { setFormTitle(e.target.value); }}
                placeholder="Brief description of the incident"
                required
                autoFocus
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="ci-description">Description</Label>
              <Textarea
                id="ci-description"
                value={formDescription}
                onChange={(e) => { setFormDescription(e.target.value); }}
                placeholder="Detailed description…"
                rows={4}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="ci-severity">
                Severity <span className="text-destructive">*</span>
              </Label>
              <Select
                value={formSeverity}
                onValueChange={(v) => { setFormSeverity(v as IncidentSeverity); }}
              >
                <SelectTrigger id="ci-severity">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="info">Info</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="ci-division">Division</Label>
                <Select
                  value={formDivisionId || 'none'}
                  onValueChange={(v) => { setFormDivisionId(v === 'none' ? '' : v); }}
                >
                  <SelectTrigger id="ci-division">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {divisions?.map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        {d.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ci-region">Region</Label>
                <Select
                  value={formRegionId || 'none'}
                  onValueChange={(v) => { setFormRegionId(v === 'none' ? '' : v); }}
                >
                  <SelectTrigger id="ci-region">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {regions?.map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </form>
        </AppSheet.Body>
        <AppSheet.Footer>
          <Button
            variant="outline"
            onClick={() => { setIsCreateOpen(false); }}
            disabled={createMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="create-incident-form"
            disabled={createMutation.isPending || !formTitle}
          >
            {createMutation.isPending ? 'Creating…' : 'Create'}
          </Button>
        </AppSheet.Footer>
      </AppSheet>
    </PageContainer>
  );
}

// ─── Severity + status badges ─────────────────────────────────────────

const STATUS_STYLE: Record<IncidentStatus, string> = {
  created: 'bg-sev-2/15 text-sev-2',
  acknowledged: 'bg-sev-5/15 text-sev-5',
  resolved: 'bg-sev-4/15 text-sev-4',
  archived: 'bg-muted text-muted-foreground',
};

/**
 * Incident rollup, in one line under the title. A parent says how many symptoms it is
 * standing in for; a rolled-up child (shown only with "Show rolled-up") says it is filed
 * under something broader — so neither reads as the whole story.
 */
function RollupNote({ incident }: { incident: Incident }) {
  if (incident.rolledUpCount) {
    return (
      <span className="text-xs text-muted-foreground">
        {incident.rolledUpCount} related {incident.rolledUpCount === 1 ? 'incident' : 'incidents'} rolled up here
      </span>
    );
  }
  if (incident.rolledUpIntoId && incident.status !== 'resolved') {
    return (
      <Link to={`/incidents/${incident.rolledUpIntoId}`} className="text-xs text-muted-foreground hover:underline">
        Rolled up into a broader incident
      </Link>
    );
  }
  return null;
}

function StatusBadge({ status }: { status: IncidentStatus }) {
  return (
    <Badge className={cn('border-0 font-normal capitalize', STATUS_STYLE[status])}>
      {status}
    </Badge>
  );
}
